import { useRef, useState } from "react"
import { Stack, Tooltip } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faChevronUp, faLock, faLockOpen } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useApiMutation } from "../../../core/api/useApiMutation"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import {
    FilteredStatuses,
    MeetingServiceOrder,
    MeetingServiceOrdersView,
} from "../../../modules/merchant/merchant/production/meetingService/meetingServiceTypes"
import {
    changeLockStatusMerchant,
    MerchantMeetingServiceOrdersQuery,
} from "../../../modules/merchant/merchant/production/meetingService/meetingServiceApi"
import { getHiddenOrderStatuses } from "../../../modules/merchant/merchant/production/meetingService/meetingServiceLib"
import { useMeetingServiceStatusUndo } from "../../../modules/merchant/merchant/production/meetingService/useMeetingServiceStatusUndo"
import { MeetingServiceOrdersDependencies } from "../../../modules/merchant/merchant/production/meetingService/components/OrderDeliveryList"
import { StatusButton } from "../../../modules/merchant/merchant/production/meetingService/components/StatusMenu"
import { STATUS_MAP } from "../../../modules/merchant/merchant/production/meetingService/constants"
import { StatusChangeModal } from "../../../modules/merchant/merchant/production/meetingService/components/StatusChangeModal"
import { StandardDialog } from "../dialogs/StandardDialog"
import { StandardButton } from "../buttons/StandardButton"

/** Gap in viewport px between the footer button and the status menu that opens above it. */
const MENU_GAP_PX = 8

const useStyles = makeStyles()((theme) => ({
    footer: {
        padding: "1rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    lockButton: {
        "& .MuiButton-startIcon svg": {
            fontSize: "1rem",
        },
    },
    lockButtonLocked: {
        "&.MuiButton-root": {
            borderColor: theme.palette.tailwind.orange[200],
            backgroundColor: theme.palette.tailwind.yellow[50],
            color: theme.palette.tailwind.yellow[700],
        },
    },
    // The footer button changes the status, so it carries the brand colour; the status colour itself
    // lives on the chip in the details card.
    statusButton: {
        "&.MuiButton-root": {
            border: "1px solid transparent",
            backgroundColor: theme.palette.tailwind.teal[500],
            color: theme.palette.common.white,
        },
        "&.MuiButton-root:hover": {
            backgroundColor: theme.palette.tailwind.teal[600],
        },
        "& .MuiButton-endIcon svg": {
            fontSize: ".875rem",
        },
    },
}))

type Props = Readonly<{
    order: MeetingServiceOrder
    view: MeetingServiceOrdersView
    meetingServiceDependencies: MeetingServiceOrdersDependencies
}>

export function MerchantOrderFooter({ order, view, meetingServiceDependencies }: Props) {
    const { classes, cx } = useStyles()
    const showStatusUndo = useMeetingServiceStatusUndo()

    const isLocked = !!order.deliveryDetails.isLocked
    const statusDetails = STATUS_MAP[order.deliveryDetails.statusDetails.status]
    const { mutate: updateLock } = useApiMutation(changeLockStatusMerchant, {
        onSuccess: (_params, _data, context) =>
            context.queryClient.invalidateQueries({ queryKey: [MerchantMeetingServiceOrdersQuery.MEETING_SERVICE] }),
    })
    const toggleLock = () => updateLock({ updateLockStatus: { isLocked: !isLocked, orderIds: [order.id] } })

    const hiddenStatuses = getHiddenOrderStatuses(view)
    const statusButtonRef = useRef<HTMLButtonElement>(null)
    const [statusAnchor, setStatusAnchor] = useState<{ top: number; left: number } | null>(null)
    const [pendingStatus, setPendingStatus] = useState<FilteredStatuses | null>(null)
    const allowedOrderIds =
        pendingStatus &&
        (order.orderAction.statusTransitions?.allowed?.[pendingStatus] ||
            !order.orderAction.statusTransitions?.disallowed?.[pendingStatus])
            ? [order.id]
            : []

    // Anchored off the button element rather than the click target: the tooltip wrapper reports a
    // different box, which dropped the menu on top of the button instead of above it.
    const onStatusClick = () => {
        const rect = statusButtonRef.current?.getBoundingClientRect()
        if (!rect) return
        setStatusAnchor((prev) => (prev ? null : { top: rect.top - MENU_GAP_PX, left: rect.right }))
    }

    return (
        <>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing="1rem"
                className={classes.footer}
            >
                <LocalizedStrict
                    id={
                        isLocked
                            ? "meeting-service-production-filter-locked-locked"
                            : "meeting-service-production-filter-locked-unlocked"
                    }
                >
                    <StandardButton
                        fullWidth={false}
                        size="medium"
                        variant="outlined"
                        color="grey"
                        className={cx(classes.lockButton, isLocked && classes.lockButtonLocked)}
                        startIcon={<FontAwesomeIcon icon={isLocked ? faLock : faLockOpen} />}
                        onClick={toggleLock}
                    >
                        {isLocked ? "Locked" : "Unlocked"}
                    </StandardButton>
                </LocalizedStrict>
                <Tooltip title={order.deliveryDetails.statusDetails.message} placement="top-end">
                    <span>
                        <LocalizedStrict id={statusDetails.stateTranslationId}>
                            <StandardButton
                                ref={statusButtonRef}
                                fullWidth={false}
                                size="medium"
                                className={classes.statusButton}
                                disabled={statusDetails.disabled}
                                endIcon={<FontAwesomeIcon icon={statusAnchor ? faChevronDown : faChevronUp} />}
                                onClick={onStatusClick}
                            >
                                {statusDetails.statusItemLabel}
                            </StandardButton>
                        </LocalizedStrict>
                    </span>
                </Tooltip>
            </Stack>

            <StatusButton
                currentStatus={order.deliveryDetails.statusDetails.status}
                hiddenStatuses={hiddenStatuses}
                anchorEl={statusAnchor}
                placement="top"
                onStatusChange={(status) => setPendingStatus(status)}
                handleClose={() => setStatusAnchor(null)}
            />

            {pendingStatus && (
                <LocalizedStrict id="meeting-service-batch-modal-title" attrs={{ dialogTitle: true }}>
                    <StandardDialog open onClose={() => setPendingStatus(null)} dialogTitle="" maxWidthProp="lg">
                        <StatusChangeModal
                            setShowUndo={(orderCount) =>
                                showStatusUndo({ status: pendingStatus, orderIds: [order.id], orderCount })
                            }
                            onClose={() => setPendingStatus(null)}
                            selectedStatus={pendingStatus}
                            allowedOrderIds={allowedOrderIds}
                            orders={[order]}
                            meetingServiceDependencies={meetingServiceDependencies}
                        />
                    </StandardDialog>
                </LocalizedStrict>
            )}
        </>
    )
}
